// IamRecruitement.jsx
import { forwardRef, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { format } from "date-fns";

/* ---------- Helpers ---------- */
const startOfToday = () => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
};
const isBefore = (a, b) => {
  if (!(a instanceof Date) || isNaN(a)) return false;
  if (!(b instanceof Date) || isNaN(b)) return false;
  return a.setHours(0, 0, 0, 0) < b.setHours(0, 0, 0, 0);
};

/* ---------- DateField with min/max ---------- */
const DateField = ({ label, value, onChange, error, minDate, maxDate }) => {
  const MuiDateInput = forwardRef(({ value, onClick }, ref) => (
    <TextField
      fullWidth
      variant="standard"
      size="medium"
      label={label}
      value={value || ""}
      onClick={onClick}
      inputRef={ref}
      error={Boolean(error)}
      helperText={error || " "}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <CalendarTodayOutlinedIcon
            fontSize="small"
            sx={{ color: "action.active", ml: 1, cursor: "pointer" }}
            onClick={onClick}
          />
        ),
      }}
    />
  ));

  return (
    <DatePicker
      selected={value}
      onChange={onChange}
      dateFormat="yyyy-MM-dd"
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      scrollableYearDropdown
      yearDropdownItemNumber={100}
      minDate={minDate}
      maxDate={maxDate}
      popperPlacement="bottom-start"
      customInput={<MuiDateInput />}
    />
  );
};

/* ---------- Theme ---------- */
const theme = createTheme({
  palette: {
    primary: { main: "#f15e22", contrastText: "#fff" },
    secondary: { main: "#2f2f2f" },
  },
  shape: { borderRadius: 14 },
});

const steps = ["Personal Details", "Travel Details", "Review"];

/* ---------- State shape ---------- */
const initialForm = {
  voornaam: "",
  familienaam: "",
  mobielNummer: "",
  emailadres: "",
  geslacht: "",
  geboortedatum: null,
  passportNummer: "",
  vertrek: "",
  aankomst: "",
  heenDatum: null,
  terugDatum: null,
};

/* ---------- Validation ---------- */
function validate(values) {
  const errors = {};
  const req = [
    "voornaam",
    "familienaam",
    "mobielNummer",
    "emailadres",
    "geslacht",
    "geboortedatum",
    "passportNummer",
    "vertrek",
    "aankomst",
    "heenDatum",
    "terugDatum",
  ];
  req.forEach((k) => {
    if (
      values[k] === null ||
      values[k] === undefined ||
      values[k] === "" ||
      (values[k] instanceof Date && isNaN(values[k].getTime()))
    ) {
      errors[k] = "Required";
    }
  });

  if (
    values.emailadres &&
    !/^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/i.test(values.emailadres)
  ) {
    errors.emailadres = "Invalid email address";
  }

  const today = startOfToday();

  // Date of birth must be in the past
  if (values.geboortedatum instanceof Date && !isNaN(values.geboortedatum)) {
    const dob = new Date(values.geboortedatum);
    if (!isBefore(dob, today)) {
      errors.geboortedatum = "Date of birth must be in the past";
    }
  }

  // Outbound and return must be today or later
  if (values.heenDatum instanceof Date && !isNaN(values.heenDatum)) {
    const heen = new Date(values.heenDatum);
    if (isBefore(heen, today)) {
      errors.heenDatum = "Outbound date must be today or later";
    }
  }
  if (values.terugDatum instanceof Date && !isNaN(values.terugDatum)) {
    const terug = new Date(values.terugDatum);
    if (isBefore(terug, today)) {
      errors.terugDatum = "Return date must be today or later";
    }
  }

  // Return must be >= outbound
  if (
    values.heenDatum instanceof Date &&
    values.terugDatum instanceof Date &&
    values.terugDatum < values.heenDatum
  ) {
    errors.terugDatum = "Return date cannot be before outbound date";
  }

  return errors;
}

/* ---------- Text Field Wrapper ---------- */
const Field = ({
  name,
  label,
  value,
  onChange,
  error,
  helperText,
  type = "text",
  ...rest
}) => (
  <TextField
    label={label}
    name={name}
    fullWidth
    variant="standard"
    size="medium"
    value={value ?? ""}
    onChange={(e) => onChange(name, e.target.value)}
    error={Boolean(error)}
    helperText={error || helperText || " "}
    type={type}
    autoComplete="off"
    {...rest}
  />
);

const ReviewRow = ({ k, v }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" py={1}>
    <Typography variant="body2" color="text.secondary">
      {k}
    </Typography>
    <Typography variant="body1" fontWeight={600}>
      {v || "—"}
    </Typography>
  </Box>
);

export default function IamRecruitement() {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "" });

  const setValue = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const payload = useMemo(
    () => ({
      ...form,
      heenDatum: form.heenDatum ? format(form.heenDatum, "yyyy-MM-dd") : null,
      terugDatum: form.terugDatum
        ? format(form.terugDatum, "yyyy-MM-dd")
        : null,
      geboortedatum: form.geboortedatum
        ? format(form.geboortedatum, "yyyy-MM-dd")
        : null,
    }),
    [form]
  );

  const next = () => {
    const v = validate(form);
    const stepFields =
      [
        [
          "voornaam",
          "familienaam",
          "mobielNummer",
          "emailadres",
          "geslacht",
          "geboortedatum",
          "passportNummer",
        ],
        ["vertrek", "aankomst", "heenDatum", "terugDatum"],
      ][activeStep] || [];

    const stepErrors = Object.keys(v).reduce((acc, k) => {
      if (stepFields.includes(k)) acc[k] = v[k];
      return acc;
    }, {});
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length === 0) setActiveStep((s) => s + 1);
  };

  const back = () => setActiveStep((s) => Math.max(0, s - 1));
  const reset = () => {
    setForm(initialForm);
    setErrors({});
    setActiveStep(0);
    setSubmitting(false);
    setSuccessId(null);
  };

  async function handleSubmit() {
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) {
      setSnack({ open: true, msg: "Please review the highlighted fields" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_HOST_URL}iam-recruitment/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error(`Server responded ${res.status}`);
      const data = await res.json();
      setSuccessId(data?.id || "—");
      setActiveStep(steps.length);
    } catch (e) {
      setSnack({ open: true, msg: "Save failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  const today = startOfToday();

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Card elevation={3}>
          <CardHeader
            title={
              <Typography variant="h5" fontWeight={800}>
                IAM Recruitment Travel Registration
              </Typography>
            }
          />
          <Divider />
          <CardContent>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step 1: Personal Details */}
            {activeStep === 0 && (
              <Box mt={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="voornaam"
                      label="First name"
                      value={form.voornaam}
                      onChange={setValue}
                      error={errors.voornaam}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="familienaam"
                      label="Last name"
                      value={form.familienaam}
                      onChange={setValue}
                      error={errors.familienaam}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="mobielNummer"
                      label="Mobile number"
                      value={form.mobielNummer}
                      onChange={setValue}
                      error={errors.mobielNummer}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="emailadres"
                      label="Email address"
                      type="email"
                      value={form.emailadres}
                      onChange={setValue}
                      error={errors.emailadres}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      variant="standard"
                      error={Boolean(errors.geslacht)}
                    >
                      <InputLabel id="geslacht-label">Gender</InputLabel>
                      <Select
                        labelId="geslacht-label"
                        value={form.geslacht}
                        onChange={(e) => setValue("geslacht", e.target.value)}
                      >
                        <MenuItem value={"M"}>M</MenuItem>
                        <MenuItem value={"F"}>F</MenuItem>
                        <MenuItem value={"X"}>X</MenuItem>
                      </Select>
                      <FormHelperText>{errors.geslacht || " "}</FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <DateField
                      label="Date of birth"
                      value={form.geboortedatum}
                      onChange={(d) => setValue("geboortedatum", d)}
                      error={errors.geboortedatum}
                      maxDate={today} // past only
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      name="passportNummer"
                      label="Passport number"
                      value={form.passportNummer}
                      onChange={setValue}
                      error={errors.passportNummer}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Step 2: Travel Details */}
            {activeStep === 1 && (
              <Box mt={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="vertrek"
                      label="Departure city or country"
                      value={form.vertrek}
                      onChange={setValue}
                      error={errors.vertrek}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="aankomst"
                      label="Arrival city or country"
                      value={form.aankomst}
                      onChange={setValue}
                      error={errors.aankomst}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <DateField
                      label="Outbound date"
                      value={form.heenDatum}
                      onChange={(d) => setValue("heenDatum", d)}
                      error={errors.heenDatum}
                      minDate={today} // today or later
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateField
                      label="Return date"
                      value={form.terugDatum}
                      onChange={(d) => setValue("terugDatum", d)}
                      error={errors.terugDatum}
                      minDate={form.heenDatum || today} // not before outbound, and future
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Step 3: Review */}
            {activeStep === 2 && (
              <Box mt={1}>
                <Typography variant="h6" gutterBottom>
                  Please review your details
                </Typography>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <ReviewRow k="First name" v={form.voornaam} />
                      <ReviewRow k="Last name" v={form.familienaam} />
                      <ReviewRow k="Mobile" v={form.mobielNummer} />
                      <ReviewRow k="Email" v={form.emailadres} />
                      <ReviewRow k="Gender" v={form.geslacht} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ReviewRow
                        k="Date of birth"
                        v={
                          form.geboortedatum
                            ? format(form.geboortedatum, "yyyy-MM-dd")
                            : ""
                        }
                      />
                      <ReviewRow k="Passport" v={form.passportNummer} />
                      <ReviewRow k="Departure" v={form.vertrek} />
                      <ReviewRow k="Arrival" v={form.aankomst} />
                      <ReviewRow
                        k="Outbound to return"
                        v={`${
                          form.heenDatum
                            ? format(form.heenDatum, "yyyy-MM-dd")
                            : ""
                        } → ${
                          form.terugDatum
                            ? format(form.terugDatum, "yyyy-MM-dd")
                            : ""
                        }`}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Box>
            )}

            {/* Success */}
            {activeStep >= steps.length && (
              <Box mt={4} textAlign="center">
                <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 72 }} />
                <Typography variant="h5" fontWeight={800} mt={2}>
                  Application sent successfully
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={1}>
                  Your reference: <b>{successId}</b>
                </Typography>
                <Box mt={3}>
                  <Button
                    onClick={reset}
                    startIcon={<ReplayIcon />}
                    variant="outlined"
                    color="primary"
                  >
                    New submission
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>

          {activeStep < steps.length && (
            <CardActions
              sx={{ display: "flex", justifyContent: "space-between", p: 3 }}
            >
              <Button disabled={activeStep === 0} onClick={back} variant="text">
                Back
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button onClick={next} variant="contained" color="primary">
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                  endIcon={
                    submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : null
                  }
                >
                  Submit
                </Button>
              )}
            </CardActions>
          )}
        </Card>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ open: false, msg: "" })}
        message={snack.msg}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setSnack({ open: false, msg: "" })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </ThemeProvider>
  );
}
